/**
 * Аккаунт, в который вошли: блоки вместо одного потока (ТЗ UI-58).
 *
 * Экран был списком равного веса: адрес, trial, «продолжить», согласие,
 * выгрузка, удаление — всё подряд. Необратимое удаление стояло в том же ряду,
 * что и возвращение к тренировке, и отличалось от него только цветом кнопки.
 *
 * Теперь блоков шесть: кто вошёл и какой доступ, продолжение, приватность,
 * выход, свои данные, опасная зона. Продолжение — единственное основное
 * действие; выгрузка вторична, удаление отделено отступом и своим фоном
 * (ТЗ UI-24). Выход стоит перед данными и опасной зоной: он ничего не
 * стирает безвозвратно и рядом с удалением читался бы страшнее, чем есть.
 */

import { useState } from 'react';
import type { MessageKey, Translate } from '@type2x/i18n';
import { Button, Card } from '@type2x/ui';
import {
  RegistrationError,
  type ProgressOutcome,
  type RegistrationProblem,
  type SignedIn,
  type VerifiedAccount,
} from '@type2x/feature-accounts';

import { AccountProblem } from './AccountProblem.js';
import { ConsentToggle } from './ConsentToggle.js';

/**
 * Что сообщить о локальном прогрессе.
 *
 * У «переносить было нечего» сообщения нет: молчание здесь точнее любой
 * фразы — прогресса в этом браузере и не было.
 */
const PROGRESS_MESSAGE: Record<ProgressOutcome, MessageKey | null> = {
  moved: 'account.claimed',
  already: 'account.progressAlready',
  taken: 'account.progressTaken',
  none: null,
};

export interface AccountSettledProps {
  readonly t: Translate;
  readonly outcome: VerifiedAccount | SignedIn;
  readonly consent: boolean;
  readonly busy: boolean;
  readonly resent: boolean;
  readonly problem: RegistrationProblem | null;
  readonly onConsentChange: (optIn: boolean) => void;
  readonly onResend: () => void;
  readonly onContinue: () => void;
  readonly onExport: () => Promise<void>;
  readonly onDelete: (password: string) => Promise<void>;
  /** Выход из аккаунта; `pending` — очередь занятий не разобрана. */
  readonly onLogout: (force?: boolean) => Promise<'done' | 'pending'>;
}

/** Карточка состоявшегося аккаунта: подтверждённого или того, в который вошли. */
export function AccountSettled({
  t,
  outcome,
  consent,
  busy,
  resent,
  problem,
  onConsentChange,
  onResend,
  onContinue,
  onExport,
  onDelete,
  onLogout,
}: AccountSettledProps): React.JSX.Element {
  const progressMessage = PROGRESS_MESSAGE[outcome.progress];
  const unverified = outcome.kind === 'signed-in' && !outcome.emailVerified;

  return (
    <Card title={t('account.title')}>
      <div className="t2x-account">
        <section className="t2x-account__block">
          {/* Под каким адресом вошли: после подтверждения с другого устройства
              «вы вошли в аккаунт» не отвечало на главный вопрос человека. */}
          <p className="t2x-account__who">
            {outcome.kind === 'verified'
              ? t('account.verified')
              : t('account.signedInAs', { email: outcome.email })}
          </p>

          <AccessState t={t} unverified={unverified} />
          {progressMessage !== null && <p>{t(progressMessage)}</p>}

          {unverified && (
            <>
              {resent && <p className="t2x-muted">{t('account.resent')}</p>}
              {problem !== null && <AccountProblem t={t} problem={problem} />}
              {/* Письмо вторично: основное действие экрана — вернуться к
                  тренировке, а не переслать письмо ещё раз. */}
              <Button variant="secondary" onClick={onResend} disabled={busy}>
                {t('account.resend')}
              </Button>
            </>
          )}
        </section>

        <section className="t2x-account__block">
          <Button onClick={onContinue}>{t('account.continue')}</Button>
        </section>

        <section className="t2x-account__block">
          <h3 className="t2x-account__heading">{t('account.privacyTitle')}</h3>
          <ConsentToggle t={t} consent={consent} onConsentChange={onConsentChange} />
        </section>

        <LogoutBlock t={t} onLogout={onLogout} />

        <DataActions t={t} onExport={onExport} onDelete={onDelete} />
      </div>
    </Card>
  );
}

/**
 * Какой доступ у аккаунта (ТЗ UI-58).
 *
 * Отсчёт дней trial отсюда убран, и вот почему. Сервер их честно считает, но
 * на доступ они не влияют ничем: платной версии нет, `FREE_ENTITLEMENTS` пуст,
 * и все режимы открыты всем и до конца trial, и после. Строка «осталось дней:
 * N» обещала человеку потерю, которой не произойдёт, и предлагала подготовиться
 * к покупке, которой не существует.
 *
 * Отсчёт вернётся вместе с платной версией: сервер его ведёт, поле
 * `trialDaysLeft` приходит по-прежнему, и показать его снова — одна строка
 * (этап ACF; оплата — Q-05).
 */
function AccessState({
  t,
  unverified,
}: {
  readonly t: Translate;
  readonly unverified: boolean;
}): React.JSX.Element {
  if (unverified) {
    return (
      <p className="t2x-account__access">
        <span className="t2x-badge t2x-badge--pending">{t('account.accessUnverified')}</span>
        <span className="t2x-muted">{t('account.unverified')}</span>
      </p>
    );
  }

  return (
    <p className="t2x-account__access">
      <span className="t2x-badge t2x-badge--neutral">{t('account.accessOpen')}</span>
      <span className="t2x-muted">{t('account.accessOpenHint')}</span>
    </p>
  );
}

/**
 * Выход из аккаунта (задание §15).
 *
 * Стоит до опасной зоны и не выглядит как она: аккаунт остаётся, навык
 * возвращается при следующем входе, и пугать здесь нечем. Подтверждения нет —
 * терять нечего.
 *
 * Кроме одного случая: итоги последних занятий могли не уйти на сервер, и
 * тогда их не вернёт никто. Об этом говорится вслух, а настоять человек
 * может — это его решение, а не наше.
 */
function LogoutBlock({
  t,
  onLogout,
}: {
  readonly t: Translate;
  readonly onLogout: (force?: boolean) => Promise<'done' | 'pending'>;
}): React.JSX.Element {
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);

  async function leave(force: boolean): Promise<void> {
    setBusy(true);
    try {
      setPending((await onLogout(force)) === 'pending');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="t2x-account__block">
      <h3 className="t2x-account__heading">{t('logout.title')}</h3>
      <p className="t2x-muted">{t('logout.explain')}</p>

      {pending && (
        <p role="alert" className="t2x-notice">
          {t('logout.pending')}
        </p>
      )}

      <Button variant="secondary" onClick={() => void leave(pending)} disabled={busy}>
        {t(pending ? 'logout.force' : 'logout.action')}
      </Button>
    </section>
  );
}

/**
 * Забрать своё и уйти (ТЗ US-08, SEC-06).
 *
 * Выгрузка и удаление разведены по двум блокам: одно отдаёт копию, другое
 * стирает оригинал, и стоять рядом им незачем. Удаление спрятано за
 * подтверждением с паролем: одна ошибочная кнопка не должна стирать год
 * работы.
 */
function DataActions({
  t,
  onExport,
  onDelete,
}: {
  readonly t: Translate;
  readonly onExport: () => Promise<void>;
  readonly onDelete: (password: string) => Promise<void>;
}): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<RegistrationProblem | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [busy, setBusy] = useState(false);

  async function attempt(action: () => Promise<void>): Promise<void> {
    setProblem(null);
    setBusy(true);
    try {
      await action();
    } catch (error) {
      setProblem(error instanceof RegistrationError ? error.problem : 'server-error');
    } finally {
      setBusy(false);
    }
  }

  if (deleted) {
    return (
      <section className="t2x-account__block t2x-account__danger">
        <p role="status">{t('privacy.deleted')}</p>
      </section>
    );
  }

  return (
    <>
      <section className="t2x-account__block">
        <h3 className="t2x-account__heading">{t('privacy.title')}</h3>
        <p className="t2x-muted">{t('privacy.explain')}</p>
        <Button variant="secondary" onClick={() => void attempt(onExport)} disabled={busy}>
          {t('privacy.export')}
        </Button>
        {problem !== null && !confirming && <AccountProblem t={t} problem={problem} />}
      </section>

      <section className="t2x-account__block t2x-account__danger">
        <h3 className="t2x-account__heading">{t('privacy.dangerTitle')}</h3>
        <p className="t2x-muted">{t('privacy.dangerExplain')}</p>

        {!confirming && (
          <Button variant="danger" onClick={() => setConfirming(true)} disabled={busy}>
            {t('privacy.delete')}
          </Button>
        )}

        {confirming && (
          <>
            <p role="alert" className="t2x-error">
              {t('privacy.deleteWarning')}
            </p>
            <label className="t2x-field">
              <span>{t('privacy.deletePassword')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <Button
              variant="danger"
              onClick={() =>
                void attempt(async () => {
                  await onDelete(password);
                  setDeleted(true);
                })
              }
              disabled={busy}
            >
              {t('privacy.deleteConfirm')}
            </Button>
          </>
        )}

        {problem !== null && confirming && <AccountProblem t={t} problem={problem} />}
      </section>
    </>
  );
}
